import React, { useEffect, useMemo, useRef, useState } from "react";

export default function ThemeColorPicker() {
    const [open, setOpen] = useState(false);
    const [textColor, setTextColor] = useState<string>("#111827");
    const [bgColor, setBgColor] = useState<string>("#FFFFFF");
    const [mode, setMode] = useState<"text" | "bg">("text");
    const rootRef = useRef<HTMLDivElement | null>(null);

    const THEME_BASES = [
        "#000000", // Dark 1
        "#E7E6E6", // Light 2
        "#44546A", // Dark 2
        "#5B9BD5", // Accent 1
        "#ED7D31", // Accent 2
        "#A5A5A5", // Accent 3
        "#FFC000", // Accent 4
        "#4472C4", // Accent 5
        "#70AD47", // Accent 6
    ];


    const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

    const hexToRgb = (hex: string) => {
        const h = hex.replace("#", "");
        const n = parseInt(h, 16);
        return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    };

    const rgbToHex = (r: number, g: number, b: number) =>
        `#${[r, g, b].map((x) => clamp(x).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

    const mix = (a: string, b: string, t: number) => {
        const A = hexToRgb(a);
        const B = hexToRgb(b);
        return rgbToHex(
            A.r + (B.r - A.r) * t,
            A.g + (B.g - A.g) * t,
            A.b + (B.b - A.b) * t
        );
    };

    // longer columns + clearer differences
    const buildColumn = (base: string) => {
        const tints = [0.85, 0.68, 0.52, 0.36].map((t) => mix(base, "#FFFFFF", t));
        const shades = [0.30, 0.48, 0.66, 0.82].map((t) => mix(base, "#000000", t));
        return [base, ...tints, ...shades]; // 1 + 4 + 4 = 9 tall
    };

    const columns = useMemo(() => THEME_BASES.map((b) => buildColumn(b)), []);

    useEffect(() => {
        const close = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("keydown", onEsc);
        };
    }, []);

    const selected = mode === "text" ? textColor : bgColor;

    const apply = (hex: string) => {
        if (mode === "text") setTextColor(hex);
        else setBgColor(hex);
        setOpen(false);
    };

    return (
        <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>


            <div style={{ display: "inline-flex", borderRadius: 14, overflow: "hidden", border: "1px solid #CBD5E1" }}>
                <button type="button" onClick={() => { setMode("text"); setOpen((v) => !v); }}
                    style={{
                        padding: "10px 12px",
                        background: mode === "text" ? "#F8FAFC" : "#FFFFFF",
                        cursor: "pointer",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>Text</span>
                </button>

                <div style={{ width: 1, background: "#CBD5E1" }} />

                <button type="button" onClick={() => { setMode("bg"); setOpen((v) => !v); }}
                    style={{
                        padding: "10px 12px",
                        background: mode === "bg" ? "#F8FAFC" : "#FFFFFF",
                        cursor: "pointer",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>Background</span>
                </button>

                <div style={{ width: 1, background: "#CBD5E1" }} />

            </div>


            {open && (
                <div style={{
                    position: "absolute",
                    top: "calc(100% + 12px)",
                    left: 0,
                    background: "#FFF",
                    border: "1px solid #CBD5E1",
                    borderRadius: 20,
                    padding: 18,
                    boxShadow: "0 30px 60px rgba(0,0,0,0.15)",
                    width: 560,
                    zIndex: 50,
                }}
                    role="menu"
                    className="ws-12"
                >
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }} className="ws-12">
                        <div style={{ fontSize: 19, fontWeight: 800 }}>Theme Colors</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#475569" }}>
                            Editing:{" "}
                            <span style={{ color: mode === "text" ? "#0F172A" : "#0F172A" }}>
                                {mode === "text" ? "Text" : "Background"}
                            </span>
                        </div>
                    </div>

                    <hr className="ws-12"></hr>

                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns.length}, 44px)`, gap: 14, marginBottom: 10, }} className="ws-12" >
                        {columns.map((col, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => apply(col[0])}
                                title={col[0]}
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    background: col[0],
                                    cursor: "pointer",
                                    border: selected === col[0] ? "3px solid #111827" : "1px solid rgba(15,23,42,0.18)",
                                }}
                            />
                        ))}
                    </div>

                    <div className="ws-12" style={{ display: "grid", gridTemplateColumns: `repeat(${columns.length}, 44px)`, gap: 14, alignItems: "start", }}  >
                        {columns.map((col, i) => (
                            <div key={i} style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(15,23,42,0.14)", background: "#FFF", }}  >
                                {col.slice(1).map((hex) => (
                                    <div key={hex} onClick={() => apply(hex)}
                                        style={{
                                            height: 30,
                                            background: hex,
                                            cursor: "pointer",
                                            outline: selected === hex ? "2px solid #111827" : "none",
                                            outlineOffset: "-2px",
                                        }}
                                        onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.filter = "brightness(0.97)")}
                                        onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.filter = "none")}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
